import React from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { colorBlue, colorAccent, colorBackground, colorFieldsBorder, colorText } from '@/utils/palette';
import { useSelector } from 'react-redux';

const MultiSelectField = ({
  name,
  label,
  options,
  defaultValue,
  placeholder,
  error,
  onChange,
  helperText,
  disabled,
}) => {
  const { mode } = useSelector((state) => ({
    mode: state?.theme?.className,
  }));

  return (
    <div>
      {label && <Label error={error}>{label}</Label>}
      <StyledSelect
        name={name}
        label={label}
        options={options}
        defaultValue={defaultValue}
        placeholder={placeholder || 'Select Options...'}
        isMulti={true}
        classNamePrefix='react-select'
        menuPortalTarget={document.body}
        styles={mode == 'light' ? customStylesLight : customStylesDark}
        maxMenuHeight={400}
        error={error ? true : false}
        onChange={onChange}
        isDisabled={disabled}
      />
      {helperText && <HelperText>{helperText}</HelperText>}
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};

MultiSelectField.displayName = 'MultiSelectField';

export default MultiSelectField;

// region STYLES
const Label = styled.div`
  font-size: 12px;
  margin-top: 5px;
  color: ${(props) => (props.error ? 'red' : colorText)};
`;

const ErrorMessage = styled.div`
  color: red;
  margin-top: -25px;
  margin-bottom: 20px;
  font-size: 12px;
`;

const HelperText = styled.div`
  font-size: 12px;
  margin-top: 5px;
  color: ${colorText};
`;

const customStylesLight = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (styles, state) => ({
    ...styles,
    backgroundColor: state.isSelected ? colorBlue : state.isFocused ? '#e6f7ff' : 'white',
    color: state.isSelected ? 'white' : state.isFocused ? 'black' : 'black',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    padding: '10px 15px',
    borderRadius: '4px',
  }),
  menu: (styles) => ({
    ...styles,
    borderRadius: '6px',
    background: 'white',
    border: `1px solid ${colorFieldsBorder}`,
    zIndex: 9999,
  }),
};

const customStylesDark = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (styles, state) => ({
    ...styles,
    backgroundColor: state.isSelected ? colorBlue : state.isFocused ? '#A9A9A9' : '#2a2a31',
    color: state.isSelected ? 'white' : state.isFocused ? 'black' : 'white',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    padding: '10px 15px',
    borderRadius: '4px',
  }),
  menu: (styles, state) => ({
    ...styles,
    borderRadius: '6px',
    background: '#2a2a31',
    border: `1px solid ${colorFieldsBorder}`,
    zIndex: 9999,
  }),
  multiValue: (styles) => ({
    ...styles,
    background: '#2a2a31',
  }),
};

const StyledSelect = styled(Select)`
  .react-select__multi-value__label {
    color: ${colorText};
  }

  width: 100%;
  margin-bottom: 25px;
  background-color: ${colorBackground} !important;
  color: black !important;
  box-shadow:
    inset 0 0 0 1px ${colorAccent},
    none !important;
  border-bottom: 1px solid ${colorFieldsBorder};
  border-radius: 0px;

  &:hover {
    border-bottom: 2px solid ${colorAccent};
    cursor: pointer;
  }

  .react-select__defaultValue {
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    margin-top: -2px;
    color: ${colorText};
  }

  .react-select__placeholder {
    font-size: 12px;
    font-family: 'Inter', sans-serif;
    margin-bottom: -12px;
    color: ${(props) => (props.error ? 'red' : colorText)} !important;
    margin-left: -8px;
  }

  .react-select__control {
    border-bottom: 1px solid ${(props) => (props.error ? 'red' : colorFieldsBorder)} !important;
    background-color: ${colorBackground};
    color: ${colorText};
    box-shadow: none !important;
    border-style: none;
    border-radius: 0px;
    transition: border-bottom 0.3s ease;

    &:hover {
      border-bottom: 1px solid ${(props) => (props.error ? 'red' : colorAccent)} !important;
      cursor: pointer;
    }
  }

  &.react-select__control--is-focused,
  &:hover {
    border-bottom: 2px solid ${colorAccent} !important;
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__indicator {
    padding: 0;
  }

  .react-select__dropdown-indicator,
  .react-select__clear-indicator {
    background-color: ${colorBackground};
    cursor: pointer;
    color: ${colorText} !important;

    svg {
      height: 22px;
      width: 22px;
    }
  }

  .react-select__single-value {
    color: ${colorText};
    margin-left: -8px;
    font-size: 12px;
  }

  .react-select__menu {
    border-radius: 1px;
    background: ${colorBackground};
    color: ${colorText};
    border-bottom: 1px solid ${colorFieldsBorder};
    z-index: 9999;

    &.react-select__menu--is-focused,
    &.react-select__menu--is-selected {
      background-color: ${colorAccent} !important;
      color: white !important;
    }

    .react-select__option {
      padding: 5px 10px;
      cursor: pointer;
      color: ${colorText};
      background: ${colorBackground};

      &.react-select__option--is-focused,
      &.react-select__option--is-selected {
        background-color: ${colorAccent} !important;
        color: white !important;
      }

      &:hover {
        background-color: ${colorAccent} !important;
        color: white !important;
      }
    }
  }
`;
